import { Breakpoint, getLatestDefinedData, Pager, ResponsiveRenderer } from "@flixbus-phx/marketplace-common";
import * as React from "react";
import CompletedRideFilter from "../../../../shared/components/completedRideFilter/CompletedRideFilter";
import getNumberForPageSize from "../../../../shared/helpers/getNumberForPageSize/getNumberForPageSize";
import getRideFilterInput from "../../../../shared/helpers/getRideFilterInput/getRideFilterInput";
import pageSizeOptions from "../../../../shared/helpers/pageSizeOptions/pageSizeOptions";
import { PageSize } from "../../../../shared/types/schema";
import usePagination, { paginationVar } from "../../../../state/usePagination/usePagination";
import useRideFilter, { rideFilterVar } from "../../../../state/useRideFilter/useRideFilter";
import useRideSort, { rideSortVar } from "../../../../state/useRideSort/useRideSort";
import useSelectedRides, { selectedRidesVar } from "../../../../state/useSelectedRides/useSelectedRides";
import { FilterRidesForRideActionsQuery, useFilterRidesForRideActionsLazyQuery } from "../../api/operations.generated";
import hasUserPermissionToChangeRides from "../../helpers/hasUserPermissionToChangeRides/hasUserPermissionToChangeRides";
import BatchOperationsDropdown from "../../ui/batchOperationsDropdown/BatchOperationsDropdown";
import RideActionsSummary from "../../ui/rideActionsSummary/RideActionsSummary";
import RideSortButton from "../../ui/rideSortButton/RideSortButton";
import * as css from "./RideActions.scss";

const RideActions: React.FC = () => {
  const [selectedRides] = useSelectedRides(selectedRidesVar);
  const [rideFilter] = useRideFilter(rideFilterVar);
  const [rideSort] = useRideSort(rideSortVar);
  const [pagination, setPagination] = usePagination(paginationVar);

  const [queryData, { data, previousData }] = useFilterRidesForRideActionsLazyQuery({
    fetchPolicy: process.env.NODE_ENV === "test" ? "network-only" : "cache-only",
  });

  React.useEffect(() => {
    queryData({
      variables: {
        pageRequest: pagination,
        rideFilter: getRideFilterInput(rideFilter, false, false),
        sort: rideSort,
      },
    });
  }, [rideFilter, rideSort]);

  const totalItems = getLatestDefinedData<FilterRidesForRideActionsQuery["filterRides"]["total"]>(
    data?.filterRides.total,
    previousData?.filterRides.total
  );

  return (
    <div className={css.grid}>
      <div>
        <RideActionsSummary
          countTotal={data?.filterRides ? data.filterRides.total : 0}
          countSelected={selectedRides.length}
          loading={data === undefined}
        />
      </div>

      <ResponsiveRenderer
        data-id="ride-actions-responsive-renderer-desktop"
        hidden={[Breakpoint.XXS, Breakpoint.XS, Breakpoint.SM, Breakpoint.MD, Breakpoint.LG]}
      >
        <div className={css.batchOpsWrapper}>
          {hasUserPermissionToChangeRides() && (
            <BatchOperationsDropdown
              selectedRides={data ? data.filterRides.items.filter((ride) => selectedRides.includes(ride.id)) : []}
            />
          )}
        </div>
        <div>
          <CompletedRideFilter />
        </div>
        <div>
          <Pager
            // Cant use loading here as it resolves to false too early with fetchPolicy cache-only: https://github.com/apollographql/apollo-client/pull/12021
            loading={totalItems === undefined}
            currentPage={pagination.page}
            onChange={(page, pageSize) => {
              setPagination({ page, pageSize: pageSize as PageSize });
              queryData({
                variables: {
                  rideFilter: getRideFilterInput(rideFilter, false, false),
                  sort: rideSort,
                  pageRequest: {
                    page,
                    pageSize: pageSize as PageSize,
                  },
                },
              });
            }}
            totalItems={totalItems}
            pageSize={{ name: pagination.pageSize, value: getNumberForPageSize(pagination.pageSize) }}
            pageSizeOptions={pageSizeOptions}
            isDropUp={false}
          />
        </div>
      </ResponsiveRenderer>

      <ResponsiveRenderer data-id="ride-actions-responsive-renderer-mobile" hidden={[Breakpoint.XL]}>
        <RideSortButton loading={totalItems === undefined} />
      </ResponsiveRenderer>
    </div>
  );
};

export default RideActions;
